import { GEOMETRY } from "@/lib/canvas-geometry";

/** Not in `src/lib`: `snapToParallelSegment` reads live edge `<line>`s out of the DOM. */

export type Pt = { x: number; y: number };

/** Wire cross-section (mm²) → stroke width in flow units. */
export function strokeWidth(mm2: number): number {
  if (mm2 <= 0.75) return 1.4;
  if (mm2 <= 1.5) return 1.6;
  if (mm2 <= 4) return 1.9;
  if (mm2 <= 10) return 2.3;
  if (mm2 <= 25) return 2.8;
  if (mm2 <= 50) return 3.3;
  return 4;
}

/** Geometric midpoint of a polyline (by total arc length). */
export function polylineMidpoint(points: Pt[]): Pt {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    total += Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  }
  let target = total / 2;
  for (let i = 0; i < points.length - 1; i++) {
    const seg = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
    if (target <= seg) {
      const t = seg === 0 ? 0 : target / seg;
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * t,
        y: points[i].y + (points[i + 1].y - points[i].y) * t,
      };
    }
    target -= seg;
  }
  return points[points.length - 1];
}

/** Returns the perpendicular, snapped onto a parallel segment of another cable if one
 * lies within `threshold` and overlaps along the primary axis. */
export function snapToParallelSegment(
  ownCableId: string,
  orientation: "h" | "v",
  axisStart: number,
  axisEnd: number,
  perp: number,
  threshold = GEOMETRY.SNAP_PARALLEL_PX,
): number {
  const lines = document.querySelectorAll<SVGLineElement>(
    `.react-flow__edges line[stroke="transparent"]`,
  );
  let best = perp;
  let bestDist: number = threshold;
  const a0 = Math.min(axisStart, axisEnd);
  const a1 = Math.max(axisStart, axisEnd);
  for (const line of Array.from(lines)) {
    const edge = line.closest<SVGGElement>(".react-flow__edge");
    if (!edge || edge.getAttribute("data-id") === ownCableId) continue;
    const x1 = parseFloat(line.getAttribute("x1") ?? "0");
    const y1 = parseFloat(line.getAttribute("y1") ?? "0");
    const x2 = parseFloat(line.getAttribute("x2") ?? "0");
    const y2 = parseFloat(line.getAttribute("y2") ?? "0");
    const horiz = Math.abs(x2 - x1) >= Math.abs(y2 - y1);
    if ((orientation === "h") !== horiz) continue;
    const otherPerp = orientation === "h" ? (y1 + y2) / 2 : (x1 + x2) / 2;
    const otherA0 = orientation === "h" ? Math.min(x1, x2) : Math.min(y1, y2);
    const otherA1 = orientation === "h" ? Math.max(x1, x2) : Math.max(y1, y2);
    if (Math.max(a0, otherA0) > Math.min(a1, otherA1) + 4) continue;
    const dist = Math.abs(otherPerp - perp);
    if (dist < bestDist) {
      bestDist = dist;
      best = otherPerp;
    }
  }
  return best;
}
